import { friendlyDbError } from '../utils/friendlyDbError';
// permissionsStore.ts
import { create } from 'zustand';
import { supabase } from '../supabase';
import { mergeLeadVisibility, type LeadVisibility, type RoleVisibilityRestrictions } from '../utils/leadVisibility';

export interface Permission {
  id: string;
  name: string;
  description: string;
  resource: string;
  action: string;
}

export interface DisabledPermission {
  resource: string;
  action: string;
}

export interface ExtraPermission {
  resource: string;
  action: string;
}

interface PermissionsState {
  permissions: Permission[];
  disabledPermissions: DisabledPermission[];
  extraPermissions: ExtraPermission[];
  globallyDisabledResources: string[];
  userRoleIds: string[];
  /** Restrizioni di visibilità sui lead fuse dai ruoli dell'utente (null = nessuna). */
  leadVisibility: LeadVisibility | null;
  loading: boolean;
  error: string | null;
  lastFetch: number | null;
  userId: string | null;
}

interface PermissionsActions {
  fetchPermissions: (userId: string, isSuperAdmin: boolean, organizationId?: string) => Promise<void>;
  clearPermissions: () => void;
  refreshPermissions: (userId: string, isSuperAdmin: boolean) => Promise<void>;
  hasPermission: (resource: string, action: string) => boolean;
  getLeadVisibility: () => LeadVisibility | null;
  hasResourceAccess: (resource: string) => boolean;
  getResourcePermissions: () => Record<string, Set<string>>;
}

type PermissionsStore = PermissionsState & PermissionsActions;

// Helper function to extract permissions from role data
function extractPermissions(data: any[]): Permission[] {
  const perms: Permission[] = [];
  const seenIds = new Set<string>();

  data?.forEach(roleData => {
    roleData.role?.permissions?.forEach((permData: any) => {
      if (permData.permission && !seenIds.has(permData.permission.id)) {
        seenIds.add(permData.permission.id);
        perms.push(permData.permission);
      }
    });
  });

  return perms;
}

// Retry logic with exponential backoff
async function retryWithBackoff<T>(
  fn: () => Promise<T>,
  maxRetries: number = 3,
  baseDelay: number = 1000
): Promise<T> {
  let lastError: any;

  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;

      if (attempt < maxRetries - 1) {
        const delay = baseDelay * Math.pow(2, attempt); // 1s, 2s, 4s
        console.log(`[PermissionsStore] Retry attempt ${attempt + 1}/${maxRetries} after ${delay}ms`);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }

  throw lastError;
}

export const usePermissionsStore = create<PermissionsStore>((set, get) => ({
  // Initial state
  permissions: [],
  disabledPermissions: [],
  extraPermissions: [],
  globallyDisabledResources: [],
  userRoleIds: [],
  leadVisibility: null,
  loading: false,
  error: null,
  lastFetch: null,
  userId: null,

  // Actions
  fetchPermissions: async (userId: string, isSuperAdmin: boolean, organizationId?: string) => {
    // Skip if already loading for this user
    const currentState = get();
    if (currentState.loading && currentState.userId === userId) {
      console.log('[PermissionsStore] Fetch already in progress, skipping');
      return;
    }

    set({ loading: true, error: null, userId });

    try {
      // Fetch permissions with retry logic
      let permissions: Permission[] = [];
      let userRoleIds: string[] = [];
      let leadVisibility: LeadVisibility | null = null;

      if (isSuperAdmin) {
        // SuperAdmin: fetch all permissions
        const result = await retryWithBackoff(async () => {
          const { data, error } = await supabase
            .from('permissions')
            .select('*');

          if (error) throw error;
          return data || [];
        });

        permissions = result;
        console.log('[PermissionsStore] Fetched all permissions for SuperAdmin:', permissions.length);
      } else {
        // Regular user: fetch permissions via roles (filtered by organization)
        const result = await retryWithBackoff(async () => {
          let query = supabase
            .from('user_roles')
            .select(`
              role:roles (
                id,
                name,
                visibility_restrictions,
                permissions:role_permissions (
                  permission:permissions (
                    id,
                    name,
                    description,
                    resource,
                    action
                  )
                )
              )
            `)
            .eq('user_id', userId);

          // Filtra per organization_id per evitare permessi da altre org
          if (organizationId) {
            query = query.eq('organization_id', organizationId);
          }

          const { data, error } = await query;
          if (error) throw error;
          return data || [];
        });

        permissions = extractPermissions(result);
        // Estrai roleIds per uso esterno (es. wiki permissions per-pagina)
        userRoleIds = (result || [])
          .map((r: any) => r?.role?.id)
          .filter((id: any): id is string => typeof id === 'string');
        // Restrizioni di visibilità (opzionali) definite nei ruoli: fuse qui una
        // volta sola, poi lette dagli store dei lead, dal kanban e dalla ricerca.
        leadVisibility = mergeLeadVisibility(
          (result || [])
            .filter((r: any) => r?.role)
            .map((r: any) => (r.role.visibility_restrictions ?? null) as RoleVisibilityRestrictions | null)
        );
        console.log('[PermissionsStore] Fetched user permissions:', permissions.length);
      }

      // Fetch disabled permissions
      const disabledResult = await retryWithBackoff(async () => {
        const { data, error } = await supabase
          .rpc('get_organization_disabled_permissions');

        if (error) throw error;
        return data || [];
      });

      console.log('[PermissionsStore] Fetched disabled permissions:', disabledResult.length);

      // Fetch globally disabled features
      const globallyDisabledResult = await retryWithBackoff(async () => {
        const { data, error } = await supabase
          .rpc('get_globally_disabled_features');

        if (error) throw error;
        return (data || []).map((r: { resource: string }) => r.resource);
      });

      console.log('[PermissionsStore] Fetched globally disabled features:', globallyDisabledResult.length);

      // Fetch extra permissions (org-level overrides that ADD permissions beyond the plan)
      const extraResult = await retryWithBackoff(async () => {
        const { data, error } = await supabase
          .rpc('get_organization_extra_permissions');

        if (error) throw error;
        return data || [];
      });

      console.log('[PermissionsStore] Fetched extra permissions:', extraResult.length);

      // Update state with fetched data
      set({
        permissions,
        disabledPermissions: disabledResult,
        extraPermissions: extraResult,
        globallyDisabledResources: globallyDisabledResult,
        userRoleIds,
        leadVisibility,
        loading: false,
        error: null,
        lastFetch: Date.now(),
        userId
      });

      // Clear accessibility cache if available
      if (typeof window !== 'undefined' && (window as any).clearAccessibilityCache) {
        (window as any).clearAccessibilityCache();
        console.log('[PermissionsStore] Accessibility cache cleared');
      }

    } catch (error: any) {
      console.error('[PermissionsStore] Error fetching permissions:', error);

      set({
        loading: false,
        error: friendlyDbError(error) || 'Failed to fetch permissions',
        // Keep existing permissions on error (fallback)
        permissions: get().permissions,
        disabledPermissions: get().disabledPermissions,
        extraPermissions: get().extraPermissions,
        globallyDisabledResources: get().globallyDisabledResources
      });
    }
  },

  clearPermissions: () => {
    console.log('[PermissionsStore] Clearing permissions');

    set({
      permissions: [],
      disabledPermissions: [],
      extraPermissions: [],
      globallyDisabledResources: [],
      userRoleIds: [],
      leadVisibility: null,
      loading: false,
      error: null,
      lastFetch: null,
      userId: null
    });

    // Clear accessibility cache if available
    if (typeof window !== 'undefined' && (window as any).clearAccessibilityCache) {
      (window as any).clearAccessibilityCache();
      console.log('[PermissionsStore] Accessibility cache cleared on clear');
    }
  },

  refreshPermissions: async (userId: string, isSuperAdmin: boolean, organizationId?: string) => {
    console.log('[PermissionsStore] Refreshing permissions for user:', userId);
    await get().fetchPermissions(userId, isSuperAdmin, organizationId);
  },

  // Selectors (computed values)
  hasPermission: (resource: string, action: string): boolean => {
    const { permissions, disabledPermissions, extraPermissions, globallyDisabledResources } = get();

    // Check if globally disabled
    if (globallyDisabledResources.includes(resource)) {
      return false;
    }

    // Check if the organization has disabled this permission
    const isDisabled = disabledPermissions.some(
      p => p.resource === resource && p.action === action
    );

    if (isDisabled) {
      return false;
    }

    // Check if user has the permission from roles
    const hasFromRole = permissions.some(
      p => p.resource === resource && p.action === action
    );

    if (hasFromRole) {
      return true;
    }

    // Check if the organization has this as an extra permission (beyond the plan)
    return extraPermissions.some(
      p => p.resource === resource && p.action === action
    );
  },

  getLeadVisibility: (): LeadVisibility | null => get().leadVisibility,

  hasResourceAccess: (resource: string): boolean => {
    const { permissions, disabledPermissions, extraPermissions, globallyDisabledResources } = get();

    // Check if globally disabled
    if (globallyDisabledResources.includes(resource)) {
      return false;
    }

    // Get all permissions for this resource (from roles + extra)
    const resourcePerms = permissions.filter(p => p.resource === resource);
    const extraResourcePerms = extraPermissions.filter(p => p.resource === resource);

    if (resourcePerms.length === 0 && extraResourcePerms.length === 0) {
      return false;
    }

    // Check if at least one action is NOT disabled
    const hasActiveFromRole = resourcePerms.some(p =>
      !disabledPermissions.some(dp => dp.resource === resource && dp.action === p.action)
    );

    if (hasActiveFromRole) {
      return true;
    }

    // Check extra permissions
    const hasActiveFromExtra = extraResourcePerms.some(p =>
      !disabledPermissions.some(dp => dp.resource === resource && dp.action === p.action)
    );

    return hasActiveFromExtra;
  },

  getResourcePermissions: (): Record<string, Set<string>> => {
    const { permissions } = get();
    const result: Record<string, Set<string>> = {};

    permissions.forEach(p => {
      if (!result[p.resource]) {
        result[p.resource] = new Set();
      }
      result[p.resource].add(p.action);
    });

    return result;
  }
}));

// Export function for backwards compatibility (used by settings components)
export function clearPermissionsCache(options?: { force?: boolean }) {
  console.log('[PermissionsStore] clearPermissionsCache called (deprecated, use store.clearPermissions)', options);
  // This is now a no-op, components should use store.refreshPermissions instead
  // Kept for backwards compatibility during migration
}

// Expose refresh function globally for debugging
if (typeof window !== 'undefined') {
  (window as any).refreshPermissions = async (userId: string, isSuperAdmin: boolean) => {
    const store = usePermissionsStore.getState();
    await store.refreshPermissions(userId, isSuperAdmin);
    console.log('[PermissionsStore] Permissions refreshed via window.refreshPermissions');
  };
}
